import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { PopoverPosition } from "@blueprintjs/core";
import { Tooltip } from "@blueprintjs/core";
import classNames from "classnames";
import { omit } from "lodash";
import { getTextContentFromHtml, renderNumberValue } from "utils/string";
import { copyToClipboard } from "utils/data";
import HtmlText from "components/HtmlText";
import { getColumnLabels, getRowName } from "../../utils";
import { bioreactorsListSelector, processVariablesSelector } from "store/global/global.selectors";
import { workspaceInsightsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, recipeCustomVariablesSelector, workspaceProcessDataSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import * as styles from "./styles.module.scss";
import * as colors from "styles/colorValues.module.scss";
const WhiskerBar = ({ columnParameter, row, bar, onCopy, value }) => {
    const processVariables = useSelector(processVariablesSelector);
    const simulations = useSelector(workspaceSimulationsSelector);
    const insights = useSelector(workspaceInsightsSelector);
    const workspaceRecipes = useSelector(workspaceRecipesSelector);
    const processDatas = useSelector(workspaceProcessDataSelector);
    const bioreactors = useSelector(bioreactorsListSelector);
    const recipeIds = useMemo(() => workspaceRecipes.map(({ id }) => id), [workspaceRecipes]);
    const recipeCustomVariables = useSelector(recipeCustomVariablesSelector(recipeIds));
    const columnLabels = useMemo(() => getColumnLabels(columnParameter, processVariables, insights, recipeCustomVariables), [columnParameter, insights, processVariables, recipeCustomVariables]);
    const rowName = useMemo(() => getRowName(row, simulations, processDatas, bioreactors), [row, simulations, processDatas, bioreactors]);
    const tooltipTitle = useMemo(() => {
        const { parameterLabel, unitsLabel } = columnLabels;
        return `${parameterLabel} ${unitsLabel ? `(${unitsLabel})` : ""} from ${rowName.title}`;
    }, [columnLabels, rowName]);
    const barSettings = useMemo(() => {
        const { min, max, firstQuartile, thirdQuartile, median } = bar;
        if (min === median && max === median && firstQuartile === median && thirdQuartile === median) {
            return;
        }
        else {
            const fullWidth = max - min;
            const minX = `${100 - ((max - firstQuartile) * 100) / fullWidth}%`;
            const medianX = `${100 - ((max - median) * 100) / fullWidth}%`;
            const rectWidth = `${((thirdQuartile - firstQuartile) * 100) / fullWidth}%`;
            return {
                minX,
                medianX,
                rectWidth,
            };
        }
    }, [bar]);
    const renderTooltipContentRow = useCallback((title, value) => (React.createElement("tr", null,
        React.createElement("td", null, "-"),
        React.createElement("td", null,
            title,
            ":"),
        React.createElement("td", null, renderNumberValue(value)))), []);
    const tooltipContent = useMemo(() => {
        const { mean, min, median, max, firstQuartile, thirdQuartile } = bar;
        return (React.createElement(React.Fragment, null,
            React.createElement(HtmlText, { targetTagName: "p", targetClassName: styles["tooltipContent-title"], text: tooltipTitle }),
            React.createElement("table", { className: styles["tooltipContent-value"] },
                React.createElement("tbody", null,
                    renderTooltipContentRow("Mean", mean),
                    renderTooltipContentRow("Median", median),
                    renderTooltipContentRow("Min", min),
                    renderTooltipContentRow("Max", max),
                    renderTooltipContentRow("Q1", firstQuartile),
                    renderTooltipContentRow("Q3", thirdQuartile)))));
    }, [bar, renderTooltipContentRow, tooltipTitle]);
    const handleCopyToClipboard = useCallback(() => {
        const { mean, median, min, max, firstQuartile, thirdQuartile } = bar;
        copyToClipboard(`${getTextContentFromHtml(tooltipTitle)} \r\n- Mean: ${mean}, \r\n- Median: ${median}, \r\n- Min: ${min}, \r\n- Max: ${max}, \r\n- Q1: ${firstQuartile}, \r\n- Q3: ${thirdQuartile}`);
        onCopy();
    }, [bar, onCopy, tooltipTitle]);
    return (React.createElement("div", { className: styles.root, onClick: handleCopyToClipboard },
        React.createElement(Tooltip, { minimal: true, openOnTargetFocus: false, className: styles.tooltipRoot, portalClassName: styles.tooltipPortal, popoverClassName: styles.tooltip, position: PopoverPosition.BOTTOM, rootBoundary: "viewport", content: tooltipContent, transitionDuration: 0, renderTarget: (props) => (React.createElement("div", { ...omit(props, "isOpen"), className: classNames(styles.tooltipTarget, props.className) })) }),
        React.createElement("p", { className: styles.mean }, renderNumberValue(value || bar.mean)),
        barSettings && (React.createElement("div", { className: styles["bar-container"] },
            React.createElement("svg", { className: styles["bar-triangle"], width: "6", height: "6", viewBox: "0 0 6 6", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                React.createElement("path", { d: "M0 6V0L6 3L0 6Z", fill: colors.s400 })),
            React.createElement("svg", { className: classNames(styles["bar-triangle"], styles["bar-triangle-max"]), width: "6", height: "6", viewBox: "0 0 6 6", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                React.createElement("path", { d: "M6 0L6 6L0 3L6 0Z", fill: colors.s400 })),
            React.createElement("svg", { className: styles.bar, fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                React.createElement("rect", { x: "0", y: "7.5", width: "100%", height: "1", fill: colors.s400 }),
                React.createElement("rect", { x: barSettings.minX, y: "2.5", width: barSettings.rectWidth, height: "11", fill: colors.white, stroke: colors.s400 }),
                React.createElement("rect", { className: styles["bar-median"], x: barSettings.medianX, width: "2", height: "16", fill: colors.black }))))));
};
export default WhiskerBar;
